import React from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import cls from 'classnames/bind';

import _get from 'lodash/get';
import _noop from 'lodash/noop';
import _isEmpty from 'lodash/isEmpty';

import ControlWithOverlay from '@ott/control-with-overlay';
import RainbowInput from '@ott/rainbow-input';

import { OFFSET_FROM_SCREEN_BOTTOM_BORDER, OFFSET_FROM_SIDE_BORDER } from './constants';

import s from './Autocomplete.scss';

const cx = cls.bind(s);

class Autocomplete extends ControlWithOverlay {
  static propTypes = {
    ...ControlWithOverlay.PropTypes,
    onText: PropTypes.func,
    onOptionSelect: PropTypes.func,
    autoSelectIfOne: PropTypes.bool,
    activeFirst: PropTypes.bool,
    isShowSpinner: PropTypes.bool,
    errorPlacement: PropTypes.oneOf(['top', 'bottom']),
    usePortalOverlay: PropTypes.bool,
    childrenClassNames: PropTypes.shape({
      trigger: PropTypes.string,
      input: PropTypes.string,
      overlay: PropTypes.string,
    }),
  };

  static defaultProps = {
    ...ControlWithOverlay.defaultProps,
    onOptionSelect: _noop,
    onText: _noop,
    autoSelectIfOne: true,
    activeFirst: true,
    valueKey: null,
    errorPlacement: 'top',
    usePortalOverlay: false,
    childrenClassNames: {
      trigger: '',
      input: '',
      overlay: '',
    },
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleEnter = this.handleEnter.bind(this);
    this.handleTab = this.handleTab.bind(this);
    this._handleSelect = this._handleSelect.bind(this);
    this.handleSelect = this.handleSelect.bind(this);

    let initialActive;

    if (props.activeFirst && _get(props.items, 'length')) {
      initialActive = props.items[0];
    }

    this.state = {
      ...this.state,
      value: props.value || props.initialValue,
      activeOption: initialActive,
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.value !== this.props.value) {
      if (nextProps.value !== this.getKey(this.state.selected, this.props.valueKey)) {
        this.setState({
          value: nextProps.value || nextProps.initialValue,
        });
      }
    }
    super.componentWillReceiveProps(nextProps);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.items !== prevProps.items) {
      this._updateActive();
      this._activeOptionReveal = true;
    }
    super.componentDidUpdate(prevProps, prevState);
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    if (this.portalRoot) {
      document.body.removeChild(this.portalRoot);
    }
    this.portalRoot = null;

    window.removeEventListener('resize', this.handleResize);
  }

  _updateActive() {
    const { items } = this.props;
    const len = _get(items, 'length');
    if (len === 1 && this.props.autoSelectIfOne && !this.state.deleting) {
      const next = items[0];
      this.handleSelect(next);
    }
  }

  setInputRef = (node) => {
    this.inputRef = node;
  };

  getOverlayPosition = () => {
    const { usePortalOverlay } = this.props;
    if (!usePortalOverlay) {
      return {};
    }

    const inputPosition = this.inputRef.getBoundingClientRect();
    return {
      top: (window.scrollY || window.pageYOffset) + inputPosition.bottom,
      left: inputPosition.left - OFFSET_FROM_SIDE_BORDER,
      right: window.innerWidth - inputPosition.right - OFFSET_FROM_SIDE_BORDER,
      maxHeight: window.innerHeight - inputPosition.bottom - OFFSET_FROM_SCREEN_BOTTOM_BORDER,
    };
  };

  handleResize = () => {
    const { isOpen } = this.state;
    if (isOpen) {
      this.setState({ position: this.getOverlayPosition() });
    }
  };

  handleChange(value) {
    const { onText } = this.props;
    const deleting =
      `${value}`.length <= `${this.state.value}`.length &&
      String.prototype.startsWith.call(`${this.state.value}`, `${value}`);

    this.setState(
      {
        isOpen: true,
        position: this.getOverlayPosition(),
        deleting,
        value,
      },
      () => {
        onText(value, this.props, this.state);
      }
    );
  }

  handleFocus = () => {
    this.setState({ position: this.getOverlayPosition() });
    return super.handleFocus();
  };

  handleBlur() {
    if (!this.state.optionFocused) {
      const value = this.props.value || (this.inputRef && this.inputRef.value);
      this.setState({ value });
    }

    clearTimeout(this._focusTO);
    this._blured = true;
    return super.handleBlur();
  }

  handleEnter(e) {
    if (e.key === ' ') {
      return;
    }
    if (!this.state.optionFocused) {
      if (this.state.selected) {
        if (this.state.value !== this.getKey(this.state.selected, this.props.valueKey)) {
          e.preventDefault();
        }
      } else {
        if (this.state.value === '') {
          e.preventDefault();
        }
      }
    }
    if (this.state.isOpen) {
      e.preventDefault();
    }
    return super.handleEnter(e);
  }

  handleTab(e) {
    const { isSelectOnTab, items } = this.props;
    const { activeOption, isOpen } = this.state;
    if (!isSelectOnTab || _isEmpty(items) || !isOpen) {
      return true;
    }
    e.preventDefault();
    if (activeOption) {
      return this.handleEnter(e);
    }

    if (!activeOption) {
      this.setState({ activeOption: items[0] }, () => this.handleEnter(e));
    }
  }

  getSelectHandler() {
    return this._handleSelect;
  }

  _handleSelect(item, props) {
    this.props.onOptionSelect(item, props);
  }

  handleSelect(item, cb) {
    const itemValue = this.getKey(item, this.props.valueKey);

    this.setState({
      value: itemValue,
    });

    this._focusTO = setTimeout(() => {
      if (this._blured) {
        this._blured = false;
        return;
      }
      if (this.inputRef && this.inputRef.focus) {
        this._forcedFocus = true;
        this.inputRef.focus();
      }
    }, 10);
    super.handleSelect(item, cb);
  }

  getTriggerProps() {
    const { placeholder, errorPlacement } = this.props;

    return {
      ...super.getTriggerProps(),
      onChange: this.handleChange,
      value: this.state.value,
      placeholder: placeholder,
      children: undefined,
      'data-error-placement': errorPlacement,
    };
  }

  getInputNode() {
    const { childrenClassNames } = this.props;

    return (
      <RainbowInput
        {...this.getTriggerProps()}
        className={cx('input', childrenClassNames.trigger)}
        parentInputRef={this.setInputRef}
        hasBottomLine={false}
      />
    );
  }

  getSpinnerNode() {
    const { isShowSpinner } = this.props;

    return (
      <div
        className={cx('spinnerWrap', {
          [`spinnerWrap--isVisible`]: isShowSpinner,
        })}
      >
        <div className={cx('spinner')} />
      </div>
    );
  }
  getOverlayNode() {
    const { isOpen, position } = this.state;
    const { childrenClassNames, usePortalOverlay } = this.props;

    if (!isOpen) {
      return null;
    }

    if (usePortalOverlay) {
      if (!this.portalRoot) {
        this.portalRoot = document.createElement('div');
        document.body.appendChild(this.portalRoot);
      }

      return createPortal(
        <div className={cx('overlay', childrenClassNames.overlay)} style={position}>
          {this.getOptions()}
        </div>,
        this.portalRoot
      );
    }

    return <div className={cx('overlay', childrenClassNames.overlay)}>{this.getOptions()}</div>;
  }

  render() {
    const { isShowSpinner } = this.props;
    const inputNode = this.getInputNode();
    const overlayNode = this.getOverlayNode();
    const spinnerNode = this.getSpinnerNode();

    return (
      <div
        className={cx('Autocomplete', {
          'Autocomplete--withSpinner': isShowSpinner,
        })}
      >
        {inputNode}
        {overlayNode}
        {spinnerNode}
      </div>
    );
  }
}

export default Autocomplete;
